import { useRef, useState } from 'react';

import { nonNullableValue } from '~shared/lib/non-nullable-value/non-nullable-value';
import { useIsDesktop } from '~shared/lib/responsive';
import { DataTable } from '~shared/ui/data-table';
import { Loader } from '~shared/ui/loader';

import { Modal } from '@breezeventures/ui-kit/modal';

import { GroupBySwitch } from './group-by-switch';
import { SummaryRowDetails } from './summary-row-details';

import { getColumns } from '../lib/columns';
import type { SummaryTableData } from '../model/summary-table-data';
import type { UseSummaryTableReturn } from '../model/use-summary-table';

type Props = {
  table: UseSummaryTableReturn;
};

export const SummaryTable = ({ table }: Props) => {
  const isDesktop = useIsDesktop();
  const [detailsOpened, setDetailsOpened] = useState(false);
  const selectedRowRef = useRef<SummaryTableData | null>(null);

  const {
    data,
    isLoading,
    groupBy,
    onGroupByChange,
    sorting,
    onSortingChange,
    columnVisibility,
    page,
    perPage,
    onPageChange,
    onPageSizeChange,
  } = table;

  const handleRowClick = (row: SummaryTableData) => {
    selectedRowRef.current = row;
    setDetailsOpened(true);
  };

  if (isLoading && !data?.items?.length) {
    return (
      <div className="relative flex min-h-[226px] flex-col">
        <Loader />
      </div>
    );
  }

  if (!data?.items?.length) {
    return null;
  }

  const responsiveProps = isDesktop
    ? { onSortingChange, minWidth: 720 }
    : {
        onRowClick: handleRowClick,
        showRowChevron: true as const,
        columnVisibility,
        minWidth: 0,
      };

  return (
    <div
      className={`
        flex min-h-[226px] flex-col gap-3
        lg:gap-6
      `}
    >
      <div className="flex items-center gap-2">
        <GroupBySwitch value={groupBy} onChange={onGroupByChange} />
      </div>

      <DataTable
        data={data.items}
        columns={getColumns(groupBy)}
        isLoading={isLoading}
        sorting={sorting}
        {...responsiveProps}
        pagination={{
          page,
          pageSize: perPage,
          itemsTotal: data.total,
          onPageChange,
          onPageSizeChange,
        }}
      />

      <Modal opened={detailsOpened} onClose={() => setDetailsOpened(false)}>
        <SummaryRowDetails row={nonNullableValue(selectedRowRef.current)} />
      </Modal>
    </div>
  );
};
